import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createTranslator, type InterfaceLanguage, type LanguageChoice, type Translate } from './languages';
import { PortalLanguage, type PortalLanguageState } from './portalLanguage';

/**
 * Tracker #119 — the Portal's interface language. The choice is held on the
 * platform and shared with SPFx and the workspace; the ordering rules (the
 * browser copy paints the first frame, the platform's value replaces it, a
 * choice made meanwhile wins) live in `PortalLanguage`.
 */
interface LanguageContextValue {
  language: InterfaceLanguage;
  /** The saved choice; null when the user never chose (Microsoft 365 → browser → English applies). */
  choice: LanguageChoice | null;
  t: Translate;
  choose: (choice: LanguageChoice) => Promise<void>;
  /** True when the last choice could not be saved (it applies in this browser for this visit). */
  saveFailed: boolean;
}

const safeStorage = (): Storage | undefined => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
};

const browserLanguages = (): string[] =>
  typeof navigator !== 'undefined' ? [...(navigator.languages ?? []), navigator.language].filter(Boolean) : [];

const createController = (settingUrl: string, onChange: (state: PortalLanguageState) => void): PortalLanguage =>
  new PortalLanguage({
    storage: safeStorage(),
    browserLanguages: browserLanguages(),
    fetchSetting: async () => {
      const response = await fetch(settingUrl, { credentials: 'include' });
      return response.ok ? response.json() : null;
    },
    saveSetting: async (choice) => {
      const response = await fetch(settingUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(choice)
      });
      return response.ok;
    },
    onChange
  });

const LanguageContext = createContext<LanguageContextValue>({
  language: 'en-us',
  choice: null,
  t: createTranslator('en-us'),
  choose: async () => undefined,
  saveFailed: false
});

export const LanguageProvider = ({ children, settingUrl = '/api/me/language' }: PropsWithChildren<{ settingUrl?: string }>): JSX.Element => {
  const controller = useRef<PortalLanguage | null>(null);
  const [state, setState] = useState<PortalLanguageState>(() => {
    controller.current = createController(settingUrl, (next) => setState(next));
    return controller.current.state;
  });

  useEffect(() => {
    const current = controller.current ?? createController(settingUrl, (next) => setState(next));
    controller.current = current;
    void current.load();
    return () => {
      current.dispose();
      controller.current = null;
    };
  }, [settingUrl]);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = state.language;
  }, [state.language]);

  const choose = useCallback(async (next: LanguageChoice) => {
    await controller.current?.choose(next);
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({ ...state, t: createTranslator(state.language), choose }),
    [state, choose]
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => useContext(LanguageContext);
